/**
 * El único lugar del frontend que llama a `fetch`.
 *
 * Todo lo demás pasa por aquí, y por eso aquí viven —una sola vez— las cuatro
 * cosas que en otro caso se repetirían en cada llamada:
 *
 *  · `credentials: 'include'`, sin el cual la cookie de sesión no viaja
 *  · la traducción del cuerpo de error del backend a un `ErrorApi` tipado
 *  · la detección de sesión caducada, que la app trata distinto de un fallo
 *  · el `Content-Type` correcto según se envíe JSON o `FormData`
 *
 * `SEG-5`: no se guarda ningún token en `localStorage`. La sesión es una
 * cookie `httpOnly` que el JavaScript no puede leer, y esa es la intención.
 */

export const BASE_API = '/api'

/**
 * Un error que el backend emitió a propósito, con su código de negocio.
 *
 * `COD-8`: `mensaje` está escrito para que lo lea un analista, así que la UI
 * lo muestra tal cual en lugar de inventar uno propio.
 */
export class ErrorApi extends Error {
  constructor(
    readonly estado: number,
    readonly codigo: string,
    mensaje: string,
  ) {
    super(mensaje)
    this.name = 'ErrorApi'
  }

  /** La sesión caducó o nunca existió: la app va a Acceso, no muestra error. */
  get esSesionCaducada(): boolean {
    return this.estado === 401
  }

  /** Cuenta con contraseña temporal: solo puede ir a Primer acceso. */
  get exigeCambioPassword(): boolean {
    return this.codigo === 'CAMBIO_PASSWORD_REQUERIDO'
  }

  get esPermiso(): boolean {
    return this.estado === 403 && !this.exigeCambioPassword
  }
}

interface CuerpoDeError {
  codigo?: unknown
  mensaje?: unknown
}

async function errorDeRespuesta(respuesta: Response): Promise<ErrorApi> {
  let cuerpo: CuerpoDeError = {}
  try {
    cuerpo = (await respuesta.json()) as CuerpoDeError
  } catch {
    // Un 502 de nginx o una caída del proceso no responden JSON.
  }
  const codigo = typeof cuerpo.codigo === 'string' ? cuerpo.codigo : 'ERROR_DESCONOCIDO'
  const mensaje =
    typeof cuerpo.mensaje === 'string'
      ? cuerpo.mensaje
      : 'No fue posible comunicarse con el servidor. Vuelva a intentarlo.'
  return new ErrorApi(respuesta.status, codigo, mensaje)
}

export interface OpcionesPeticion {
  metodo?: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE'
  cuerpo?: unknown
  formulario?: FormData
  senal?: AbortSignal
}

export async function peticion<T>(ruta: string, opciones: OpcionesPeticion = {}): Promise<T> {
  const { metodo = 'GET', cuerpo, formulario, senal } = opciones

  const cabeceras: Record<string, string> = {}
  let body: BodyInit | undefined
  if (formulario) {
    // Deliberadamente SIN `Content-Type`: el navegador tiene que ponerlo él
    // para incluir el `boundary` del multipart.
    body = formulario
  } else if (cuerpo !== undefined) {
    cabeceras['Content-Type'] = 'application/json'
    body = JSON.stringify(cuerpo)
  }

  const respuesta = await fetch(`${BASE_API}${ruta}`, {
    method: metodo,
    headers: cabeceras,
    credentials: 'include',
    ...(body ? { body } : {}),
    ...(senal ? { signal: senal } : {}),
  })

  if (!respuesta.ok) throw await errorDeRespuesta(respuesta)
  if (respuesta.status === 204) return undefined as T

  const texto = await respuesta.text()
  return (texto ? JSON.parse(texto) : undefined) as T
}

export const api = {
  obtener: <T,>(ruta: string, senal?: AbortSignal): Promise<T> =>
    peticion<T>(ruta, senal ? { senal } : {}),
  crear: <T,>(ruta: string, cuerpo?: unknown): Promise<T> => peticion<T>(ruta, { metodo: 'POST', cuerpo }),
  reemplazar: <T,>(ruta: string, cuerpo: unknown): Promise<T> => peticion<T>(ruta, { metodo: 'PUT', cuerpo }),
  modificar: <T,>(ruta: string, cuerpo: unknown): Promise<T> => peticion<T>(ruta, { metodo: 'PATCH', cuerpo }),
  eliminar: <T,>(ruta: string): Promise<T> => peticion<T>(ruta, { metodo: 'DELETE' }),
  subir: <T,>(ruta: string, formulario: FormData): Promise<T> =>
    peticion<T>(ruta, { metodo: 'POST', formulario }),
}

/** Construye `?a=1&b=2` omitiendo lo vacío, que es siempre lo que se quiere. */
export function query(parametros: Readonly<Record<string, string | number | boolean | undefined>>): string {
  const partes = Object.entries(parametros).filter(
    ([, valor]) => valor !== undefined && valor !== '',
  )
  if (partes.length === 0) return ''
  return `?${new URLSearchParams(partes.map(([clave, valor]) => [clave, String(valor)])).toString()}`
}

/** URL de descarga directa. La sirve el navegador con la cookie de sesión. */
export function urlDeArchivo(evidenciaId: string): string {
  return `${BASE_API}/evidencias/${evidenciaId}/archivo`
}

export function urlDeOficio(consultaId: string): string {
  return `${BASE_API}/circuito/oficios/${consultaId}/documento`
}

/** El oficio para numerar y firmar en el trámite documentario. */
export function urlDeProyectoOficio(requerimientoId: string): string {
  return `${BASE_API}/requerimientos/${requerimientoId}/oficio`
}

/** Un memorando para numerar y firmar, dibujado desde lo que se congeló al emitirlo. */
export function urlDeMemorando(memorandoId: string): string {
  return `${BASE_API}/memorandos/${memorandoId}/pdf`
}

/** El acta del sorteo: la oficial si ya se confirmó; si no, un borrador sin número. */
export function urlDeActa(sorteoId: string): string {
  return `${BASE_API}/sorteos/${sorteoId}/acta`
}
